import { Injectable } from '@angular/core';
import { DataService } from './data-service';
import { FirebaseFunctionNames } from '../constants/firebase-function-names';

// Requests
import { GetRubricRequest } from '../models/requests/get-rubric-request';
import { SaveRubricRequest } from '../models/requests/save-rubric-request';
import { CreatePdfRequest } from '../models/requests/create-pdf-request';
import { CompleteRubricRequest } from '../models/requests/complete-rubric-request';
import { GetRubricsRequest } from '../models/requests/get-rubrics-request';
import { ValidateReferenceRequest } from '../models/requests/validate-reference-request';
import { DeleteRubricRequest } from '../models/requests/delete-rubric-request';
import { CreateExcelReportRequest } from '../models/requests/create-excel-report-request';
import { GetUsersRequest } from '../models/requests/get-users-request';
import { SaveChecklistRequest } from '../models/requests/save-checklist-request';
import { GetChecklistRequest } from '../models/requests/get-checklist-request';
import { GetCoursesRequest } from '../models/requests/get-courses-request';
import { GetCourseSectionsRequest } from '../models/requests/get-course-sections-request';
import { GetAnnouncementsRequest } from '../models/requests/get-announcements-request';
import { GetCanvasUserProfileRequest } from '../models/requests/get-canvas-user-profile-request';
import { GetAssignmentsRequest } from '../models/requests/get-assignments-request';
import { GetAssignmentRequest } from '../models/requests/get-assignment-request';
import { BookmarkAssignmentRequest } from '../models/requests/bookmark-assignment-request';
import { GetAssignmentBookmarksRequest } from '../models/requests/get-assignment-bookmarks-request';
import { GetSubmissionRequest } from '../models/requests/get-submission-request';
import { SyncSubmissionsRequest } from '../models/requests/sync-submissions-request';
import { GetSubmissionsRequest } from '../models/requests/get-submissions-request';
import { PostSubmissionCommentRequest } from '../models/requests/post-submission-comment-request';
import { PostWorklistSubmissionCommentRequest } from '../models/requests/post-worklist-submission-comment-request';

// Responses
import { GetRubricResponse } from '../models/responses/get-rubric-response';
import { SaveRubricResponse } from '../models/responses/save-rubric-response';
import { CreatePdfResponse } from '../models/responses/create-pdf-response';
import { CompleteRubricResponse } from '../models/responses/complete-rubric-response';
import { GetRubricsResponse } from '../models/responses/get-rubrics-response';
import { ValidateReferenceResponse } from '../models/responses/validate-reference-response';
import { DeleteRubricResponse } from '../models/responses/delete-rubric-response';
import { CreateExcelReportResponse } from '../models/responses/create-excel-report-response';
import { GetUsersResponse } from '../models/responses/get-users-response';
import { SaveChecklistResponse } from '../models/responses/save-checklist-response';
import { GetChecklistResponse } from '../models/responses/get-checklist-response';
import { GetCoursesResponse } from '../models/responses/get-courses-response';
import { GetCourseSectionsResponse } from '../models/responses/get-course-sections-response';
import { GetAnnouncementsResponse } from '../models/responses/get-announcements-response';
import { GetCanvasUserProfileResponse } from '../models/responses/get-canvas-user-profile-response';
import { GetAssignmentsResponse } from '../models/responses/get-assignments-response';
import { GetAssignmentResponse } from '../models/responses/get-assignment-response';
import { BookmarkAssignmentResponse } from '../models/responses/bookmark-assignment-response';
import { GetAssignmentBookmarksResponse } from '../models/responses/get-assignment-bookmarks-response';
import { GetSubmissionResponse } from '../models/responses/get-submission-response';
import { SyncSubmissionsResponse } from '../models/responses/sync-submissions-response';
import { GetSubmissionsResponse } from '../models/responses/get-submissions-response';
import { PostSubmissionCommentResponse } from '../models/responses/post-submission-comment-response';
import { PostWorklistSubmissionCommentResponse } from '../models/responses/post-worklist-submission-comment-response';
import { GetAccountRequest } from '../models/requests/get-account-request';
import { GetAccountResponse } from '../models/responses/get-account-response';
import { UpdateAccountRequest } from '../models/requests/update-account-request';
import { UpdateAccountResponse } from '../models/responses/update-account-response';
import { GetAttachmentRequest } from '../models/requests/get-attachment-request';
import { GetAttachmentResponse } from '../models/responses/get-attachment-response';
import { ChatGPTRequest } from '../models/requests/chat-gpt/chat-gpt-request';
import { ChatGPTResponse } from '../models/responses/chat-gpt/chat-gpt-response';
import { GetRecentlyViewedSubmissionsRequest } from '../models/requests/get-recently-viewed-submissions-request';
import { GetGoogleAdsRequest } from '../models/requests/playwright/get-google-ads-request';
import { GetAdditionalUserInfoRequest } from '../models/requests/get-additional-user-info-request';
import { GetAdditionalUserInfoResponse } from '../models/responses/get-additional-user-info-response';
import { PostSubmissionAssessmentResponse } from '../models/responses/post-submission-assessment-response';
import { PostSubmissionAssessmentRequest } from '../models/requests/post-submission-assessment-request';
import { CanvasSubmitAssessmentResponse } from '../models/responses/canvas-submit-assessment-response';
import { CanvasSubmitAssessmentRequest } from '../models/requests/canvas-submit-assessment-request';
import { GetWorklistsRequest } from '../models/requests/get-worklists-request';
import { GetWorklistsResponse } from '../models/responses/get-worklists-response';
import { PrepareAllocationsRootRequest } from '../models/requests/prepare-allocations-root-request';
import { PrepareAllocationsRootResponse } from '../models/responses/prepare-allocations-root-response';
import { InitialiseAllocationsRequest } from '../models/requests/initialise-allocations-request';
import { InitialiseAllocationsResponse } from '../models/responses/initialise-allocations-response';
import { GetCanvasAssignmentAllocationTraysRequest } from '../models/requests/get-canvas-assignment-allocation-trays-request';
import { GetCanvasAssignmentAllocationTraysResponse } from '../models/responses/get-canvas-assignment-allocation-trays-response';
import { GetCanvasAssignmentAllocationTraySubmissionsRequest } from '../models/requests/get-canvas-assignment-allocation-tray-submissions-request';
import { GetCanvasAssignmentAllocationTraySubmissionsResponse } from '../models/responses/get-canvas-assignment-allocation-tray-submissions-response';
import { GetCanvasAssignmentSubmissionsByMarkerIdRequest } from '../models/requests/get-canvas-assignment-submissions-by-marker-id-request';
import { GetCanvasAssignmentSubmissionsByMarkerIdResponse } from '../models/responses/get-canvas-assignment-submissions-by-marker-id-response';
import { GetWorklistSubmissionsRequest } from '../models/requests/get-worklist-submissions-request';
import { GetWorklistSubmissionsResponse } from '../models/responses/get-worklist-submissions-response';
import { GetWorklistSubmissionRequest } from '../models/requests/get-worklist-submission-request';
import { GetWorklistSubmissionResponse } from '../models/responses/get-worklist-submission-response';
import { GetWorklistRequest } from '../models/requests/get-worklist-request';
import { GetWorklistResponse } from '../models/responses/get-worklist-response';
import { GetMarkerFeedbackTemplateRequest } from '../models/requests/get-marker-feedback-template-request';
import { GetMarkerFeedbackTemplateResponse } from '../models/responses/get-marker-feedback-template-response';
import { SaveMarkerFeedbackTemplateRequest } from '../models/requests/save-marker-feedback-template-request';
import { SaveMarkerFeedbackTemplateResponse } from '../models/responses/save-marker-feedback-template-response';
import { GetAudioAssetsRequest } from '../models/requests/get-audio-assets-request';
import { GetAudioAssetsResponse } from '../models/responses/get-audio-assets-response';
import { GetAudioAssetRequest } from '../models/requests/get-audio-asset-request';
import { GetAudioAssetResponse } from '../models/responses/get-audio-asset-response';
import { DeleteAudioAssetRequest } from '../models/requests/delete-audio-asset-request';
import { DeleteAudioAssetResponse } from '../models/responses/delete-audio-asset-response';
import { MigrateAudioAssetsToStorageRequest } from '../models/requests/migrate-audio-assets-to-storage-request';
import { MigrateAudioAssetsToStorageResponse } from '../models/responses/migrate-audio-assets-to-storage-response';
import { GetElevenLabsVoicesRequest } from '../models/requests/get-elevenlabs-voices-request';
import { GetElevenLabsVoicesResponse } from '../models/responses/get-elevenlabs-voices-response';
import { PreviewElevenLabsVoiceRequest } from '../models/requests/preview-elevenlabs-voice-request';
import { PreviewElevenLabsVoiceResponse } from '../models/responses/preview-elevenlabs-voice-response';
import { SaveElevenLabsVoiceAssetRequest } from '../models/requests/save-elevenlabs-voice-asset-request';
import { SaveElevenLabsVoiceAssetResponse } from '../models/responses/save-elevenlabs-voice-asset-response';
import { SaveFeedbackTemplateRequest } from '../models/requests/save-feedback-template-request';
import { SaveFeedbackTemplateResponse } from '../models/responses/save-feedback-template-response';
import { GetFeedbackTemplatesRequest } from '../models/requests/get-feedback-templates-request';
import { GetFeedbackTemplatesResponse } from '../models/responses/get-feedback-templates-response';
import { GetFeedbackTemplateRequest } from '../models/requests/get-feedback-template-request';
import { GetFeedbackTemplateResponse } from '../models/responses/get-feedback-template-response';
import { GetEnabledFeedbackTemplateRequest } from '../models/requests/get-enabled-feedback-template-request';
import { GetEnabledFeedbackTemplateResponse } from '../models/responses/get-enabled-feedback-template-response';
import { EnableFeedbackTemplateRequest } from '../models/requests/enable-feedback-template-request';
import { EnableFeedbackTemplateResponse } from '../models/responses/enable-feedback-template-response';
import { GetMarkersRequest } from '../models/requests/get-markers-request';
import { GetMarkersResponse } from '../models/responses/get-markers-response';
import { SaveAgentRequest } from '../models/requests/save-agent-request';
import { SaveAgentResponse } from '../models/responses/save-agent-response';
import { GetAgentsRequest } from '../models/requests/get-agents-request';
import { GetAgentsResponse } from '../models/responses/get-agents-response';
import { GetAgentForMarkerRequest } from '../models/requests/get-agent-for-marker-request';
import { GetAgentForMarkerResponse } from '../models/responses/get-agent-for-marker-response';
import { CreateThreadRequest } from '../models/requests/create-thread-request';
import { CreateThreadResponse } from '../models/responses/create-thread-response';
import { GetThreadRequest } from '../models/requests/get-thread-request';
import { GetThreadResponse } from '../models/responses/get-thread-response';
import { GetThreadMessagesRequest } from '../models/requests/get-thread-messages-request';
import { GetThreadMessagesResponse } from '../models/responses/get-thread-messages-response';
import { SendThreadMessageRequest } from '../models/requests/send-thread-message-request';
import { SendThreadMessageResponse } from '../models/responses/send-thread-message-response';
import { DeleteThreadRequest } from '../models/requests/delete-thread-request';
import { DeleteThreadResponse } from '../models/responses/delete-thread-response';
import { GenerateRatingFeedbackRequest } from '../models/requests/generate-rating-feedback-request';
import { GenerateRatingFeedbackResponse } from '../models/responses/generate-rating-feedback-response';
import { GetStudentPerformanceRequest } from '../models/requests/get-student-performance-request';
import { GetStudentPerformanceResponse } from '../models/responses/get-student-performance-response';
import { CheckPhoneCodeAttemptsRequest } from '../models/requests/check-phone-code-attempts-request';
import { CheckPhoneCodeAttemptsResponse } from '../models/responses/check-phone-code-attempts-response';
import { SendPhoneVerificationCodeRequest } from '../models/requests/send-phone-verification-code-request';
import { SendPhoneVerificationCodeResponse } from '../models/responses/send-phone-verification-code-response';
import { VerifyPhoneCodeRequest } from '../models/requests/verify-phone-code-request';
import { VerifyPhoneCodeResponse } from '../models/responses/verify-phone-code-response';
import { SendEmailLinkRequest } from '../models/requests/send-email-link-request';
import { SendEmailLinkResponse } from '../models/responses/send-email-link-response';
import { VerifyEmailLinkRequest } from '../models/requests/verify-email-link-request';
import { VerifyEmailLinkResponse } from '../models/responses/verify-email-link-response';

@Injectable({ providedIn: 'root' })
export class PersistenceService extends DataService {
  protected createFunctions(): void {
    // No-op
  }

  public getRubric(req: GetRubricRequest): Promise<GetRubricResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getRubric, req);
  }

  public saveRubric(req: SaveRubricRequest): Promise<SaveRubricResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.saveRubric, req);
  }

  public createPdf(req: CreatePdfRequest): Promise<CreatePdfResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.createPdf, req);
  }

  public completeRubric(req: CompleteRubricRequest): Promise<CompleteRubricResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.completeRubric, req);
  }

  public getRubrics(req: GetRubricsRequest): Promise<GetRubricsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getRubrics, req);
  }

  public validateReference(req: ValidateReferenceRequest): Promise<ValidateReferenceResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.validateReference, req);
  }

  public deleteRubric(req: DeleteRubricRequest): Promise<DeleteRubricResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.deleteRubric, req);
  }

  public createExcelReport(req: CreateExcelReportRequest): Promise<CreateExcelReportResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.createExcelReport, req);
  }

  public getUsers(req: GetUsersRequest): Promise<GetUsersResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getUsers, req);
  }


  public saveChecklist(req: SaveChecklistRequest): Promise<SaveChecklistResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.saveChecklist, req);
  }

  public getChecklist(req: GetChecklistRequest): Promise<GetChecklistResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getChecklist, req);
  }

  public getCourses(req: GetCoursesRequest): Promise<GetCoursesResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getCourses, req);
  }

  public getCourseSections(req: GetCourseSectionsRequest): Promise<GetCourseSectionsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getCourseSections, req);
  }

  public getCanvasUserProfile(req: GetCanvasUserProfileRequest): Promise<GetCanvasUserProfileResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getCanvasUserProfile, req);
  }

  public getAssignments(req: GetAssignmentsRequest): Promise<GetAssignmentsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAssignments, req);
  }

  public getAssignment(req: GetAssignmentRequest): Promise<GetAssignmentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAssignment, req);
  }

  public getSubmissions(req: GetSubmissionsRequest): Promise<GetSubmissionsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getSubmissions, req);
  }

  public getSubmission(req: GetSubmissionRequest): Promise<GetSubmissionResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getSubmission, req);
  }

  public bookmarkAssignment(req: BookmarkAssignmentRequest): Promise<BookmarkAssignmentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.bookmarkAssignment, req);
  }

  public getAssignmentBookmarks(req: GetAssignmentBookmarksRequest): Promise<GetAssignmentBookmarksResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAssignmentBookmarks, req);
  }

  public syncSubmissions(req: SyncSubmissionsRequest): Promise<SyncSubmissionsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.syncSubmissions, req);
  }

  public postSubmissionComment(req: PostSubmissionCommentRequest): Promise<PostSubmissionCommentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.postSubmissionComment, req);
  }

  public postWorklistSubmissionComment(req: PostWorklistSubmissionCommentRequest): Promise<PostWorklistSubmissionCommentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.postWorklistSubmissionComment, req);
  }

  public getAccount(req: GetAccountRequest): Promise<GetAccountResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAccount, req);
  }

  public updateAccount(req: UpdateAccountRequest): Promise<UpdateAccountResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.updateAccount, req);
  }

  public getAttachment(req: GetAttachmentRequest): Promise<GetAttachmentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAttachment, req);
  }

  public chatGPT(req: ChatGPTRequest): Promise<ChatGPTResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.chatGPT, req);
  }

  public getRecentlyViewedSubmissions(req: GetRecentlyViewedSubmissionsRequest): Promise<any> {
    return this.callFunctionAsync(FirebaseFunctionNames.getRecentlyViewedSubmissions, req);
  }

  public getAnnouncements(req: GetAnnouncementsRequest): Promise<GetAnnouncementsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAnnouncements, req);
  }

  public getGoogleAds(req: GetGoogleAdsRequest): Promise<any> {
    return this.callFunctionAsync(FirebaseFunctionNames.getGoogleAds, req);
  }

  public getAdditionalUserInfo(req: GetAdditionalUserInfoRequest): Promise<GetAdditionalUserInfoResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAdditionalUserInfo, req);
  }

  public postSubmissionAssessment(req: PostSubmissionAssessmentRequest): Promise<PostSubmissionAssessmentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.postSubmissionAssessment, req);
  }

  public canvasSubmitAssessment(req: CanvasSubmitAssessmentRequest): Promise<CanvasSubmitAssessmentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.canvasSubmitAssessment, req);
  }

  public getWorklists(req: GetWorklistsRequest): Promise<GetWorklistsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getWorklists, req);
  }

  public prepareAllocationsRoot(req: PrepareAllocationsRootRequest): Promise<PrepareAllocationsRootResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.prepareAllocationsRoot, req);
  }

  public initialiseAllocations(req: InitialiseAllocationsRequest): Promise<InitialiseAllocationsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.initialiseAllocations, req);
  }

  public getCanvasAssignmentAllocationTrays(req: GetCanvasAssignmentAllocationTraysRequest): Promise<GetCanvasAssignmentAllocationTraysResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getCanvasAssignmentAllocationTrays, req);
  }

  public getCanvasAssignmentAllocationTraySubmissions(req: GetCanvasAssignmentAllocationTraySubmissionsRequest): Promise<GetCanvasAssignmentAllocationTraySubmissionsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getCanvasAssignmentAllocationTraySubmissions, req);
  }

  public getCanvasAssignmentSubmissionsByMarkerId(req: GetCanvasAssignmentSubmissionsByMarkerIdRequest): Promise<GetCanvasAssignmentSubmissionsByMarkerIdResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getCanvasAssignmentSubmissionsByMarkerId, req);
  }

  public getWorklistSubmissions(req: GetWorklistSubmissionsRequest): Promise<GetWorklistSubmissionsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getWorklistSubmissions, req);
  }

  public getWorklistSubmission(req: GetWorklistSubmissionRequest): Promise<GetWorklistSubmissionResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getWorklistSubmission, req);
  }

  public getWorklist(req: GetWorklistRequest): Promise<GetWorklistResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getWorklist, req);
  }

  public getMarkerFeedbackTemplate(req: GetMarkerFeedbackTemplateRequest): Promise<GetMarkerFeedbackTemplateResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getMarkerFeedbackTemplate, req);
  }

  public saveMarkerFeedbackTemplate(req: SaveMarkerFeedbackTemplateRequest): Promise<SaveMarkerFeedbackTemplateResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.saveMarkerFeedbackTemplate, req);
  }

  public getAudioAssets(req: GetAudioAssetsRequest): Promise<GetAudioAssetsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAudioAssets, req);
  }

  public getAudioAsset(req: GetAudioAssetRequest): Promise<GetAudioAssetResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAudioAsset, req);
  }

  public deleteAudioAsset(req: DeleteAudioAssetRequest): Promise<DeleteAudioAssetResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.deleteAudioAsset, req);
  }

  public migrateAudioAssetsToStorage(req: MigrateAudioAssetsToStorageRequest): Promise<MigrateAudioAssetsToStorageResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.migrateAudioAssetsToStorage, req);
  }

  public getElevenLabsVoices(req: GetElevenLabsVoicesRequest): Promise<GetElevenLabsVoicesResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getElevenLabsVoices, req);
  }

  public previewElevenLabsVoice(req: PreviewElevenLabsVoiceRequest): Promise<PreviewElevenLabsVoiceResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.previewElevenLabsVoice, req);
  }

  public saveElevenLabsVoiceAsset(req: SaveElevenLabsVoiceAssetRequest): Promise<SaveElevenLabsVoiceAssetResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.saveElevenLabsVoiceAsset, req);
  }

  public saveFeedbackTemplate(req: SaveFeedbackTemplateRequest): Promise<SaveFeedbackTemplateResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.saveFeedbackTemplate, req);
  }

  public getFeedbackTemplates(req: GetFeedbackTemplatesRequest): Promise<GetFeedbackTemplatesResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getFeedbackTemplates, req);
  }
  public getFeedbackTemplate(req: GetFeedbackTemplateRequest): Promise<GetFeedbackTemplateResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getFeedbackTemplate, req);
  }
  public getEnabledFeedbackTemplate(req: GetEnabledFeedbackTemplateRequest): Promise<GetEnabledFeedbackTemplateResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getEnabledFeedbackTemplate, req);
  }
  public enableFeedbackTemplate(req: EnableFeedbackTemplateRequest): Promise<EnableFeedbackTemplateResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.enableFeedbackTemplate, req);
  }

  public getStudentPerformance(req: GetStudentPerformanceRequest): Promise<GetStudentPerformanceResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getStudentPerformance, req);
  }

  public getMarkers(req: GetMarkersRequest): Promise<GetMarkersResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getMarkers, req);
  }

  public saveAgent(req: SaveAgentRequest): Promise<SaveAgentResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.saveAgent, req);
  }

  public getAgents(req: GetAgentsRequest): Promise<GetAgentsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAgents, req);
  }

  public getAgentForMarker(req: GetAgentForMarkerRequest): Promise<GetAgentForMarkerResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getAgentForMarker, req);
  }

  public createThread(req: CreateThreadRequest): Promise<CreateThreadResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.createThread, req);
  }

  public getThread(req: GetThreadRequest): Promise<GetThreadResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getThread, req);
  }

  public getThreadMessages(req: GetThreadMessagesRequest): Promise<GetThreadMessagesResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.getThreadMessages, req);
  }

  public sendThreadMessage(req: SendThreadMessageRequest): Promise<SendThreadMessageResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.sendThreadMessage, req);
  }

  public deleteThread(req: DeleteThreadRequest): Promise<DeleteThreadResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.deleteThread, req);
  }

  public generateRatingFeedback(req: GenerateRatingFeedbackRequest): Promise<GenerateRatingFeedbackResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.generateRatingFeedback, req);
  }

  public checkPhoneCodeAttempts(req: CheckPhoneCodeAttemptsRequest): Promise<CheckPhoneCodeAttemptsResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.checkPhoneCodeAttempts, req);
  }

  public sendPhoneVerificationCode(req: SendPhoneVerificationCodeRequest): Promise<SendPhoneVerificationCodeResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.sendPhoneVerificationCode, req);
  }

  public verifyPhoneCode(req: VerifyPhoneCodeRequest): Promise<VerifyPhoneCodeResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.verifyPhoneCode, req);
  }

  public sendEmailLink(req: SendEmailLinkRequest): Promise<SendEmailLinkResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.sendEmailLink, req);
  }

  public verifyEmailLink(req: VerifyEmailLinkRequest): Promise<VerifyEmailLinkResponse> {
    return this.callFunctionAsync(FirebaseFunctionNames.verifyEmailLink, req);
  }
}
